/**
 * followState — pure reducer for the transcript "Following" auto-scroll toggle.
 *
 * State machine:
 *   - following: true  → auto-scroll is active; active turn scrolls into view
 *   - following: false → user manually scrolled away; auto-scroll paused,
 *                        "Follow" affordance shown in PlayerDock
 *
 * Transitions:
 *   "manual_scroll"  → following becomes false (user scrolled away)
 *   "reengage"       → following becomes true (user clicked Follow button)
 *   "programmatic"   → no change (flag-guarded programmatic scrollIntoView)
 */
export type FollowAction = "manual_scroll" | "reengage" | "programmatic";

export interface FollowState {
  following: boolean;
}

export const INITIAL_FOLLOW_STATE: FollowState = { following: true };

export function followReducer(state: FollowState, action: FollowAction): FollowState {
  switch (action) {
    case "manual_scroll":
      if (!state.following) return state; // already disengaged, no-op
      return { following: false };
    case "reengage":
      if (state.following) return state; // already engaged, no-op
      return { following: true };
    case "programmatic":
      // Programmatic scrolls (from scrollIntoView) must not disengage follow.
      // This action is a no-op in the reducer; it exists so callers can
      // unconditionally dispatch without branching.
      return state;
    default:
      return state;
  }
}

/**
 * isClickSeek — guard predicate for click-to-seek on transcript tokens.
 *
 * Returns true when the click should trigger a seek. Returns false when:
 *   - There is a non-empty text selection (user is selecting text for
 *     the CorrectionPopover — we must not treat that as a seek click)
 *
 * Usage: call inside the token span's onClick before seeking.
 */
export function isClickSeek(): boolean {
  const sel = window.getSelection();
  if (!sel) return true; // no selection API — treat as click
  if (sel.isCollapsed) return true; // collapsed selection = simple click
  return sel.toString().trim().length === 0; // whitespace-only = treat as click
}

/**
 * Programmatic-scroll guard window.
 *
 * scrollIntoView({ behavior: "smooth" }) fires a STREAM of scroll events
 * over the whole animation (hundreds of ms), not a single event. A one-shot
 * boolean flag only swallows the first event — the rest of the burst would
 * be misread as manual scrolling and falsely disengage Following (live bug
 * found 2026-06-12). Instead, callers record Date.now() when they initiate
 * a programmatic scroll, and the scroll listener ignores every event inside
 * the window. Chrome's smooth scroll settles well under a second even for
 * long distances; 1200ms gives margin without meaningfully swallowing real
 * user scrolls (auto-scrolls happen at paragraph boundaries, several
 * seconds apart).
 */
export const PROGRAMMATIC_SCROLL_WINDOW_MS = 1200;

/**
 * isWithinProgrammaticWindow — true when a scroll event at `now` should be
 * attributed to the programmatic scroll initiated at `markedAt`.
 * Pure for testability; markedAt = 0 means "never marked".
 */
export function isWithinProgrammaticWindow(
  markedAt: number,
  now: number,
  windowMs: number = PROGRAMMATIC_SCROLL_WINDOW_MS,
): boolean {
  if (markedAt <= 0) return false;
  return now - markedAt < windowMs;
}
